import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from '@headlessui/react';
import {
  XCircleIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/solid';
import { twMerge } from 'tailwind-merge';

interface IProps {
  variant: 'success' | 'error' | 'warning' | 'info';
  open: boolean;
  onClose: (open: boolean) => void;
  onConfirm?: () => void;
  title: string;
  description?: string;
  className?: string;
  closeLabel?: string;
  confirmLabel?: string;
  dataTestId?: string;
}

export default function AlertDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  className,
  closeLabel = 'Cancel',
  confirmLabel = 'Confirm',
  variant = 'info',
  dataTestId,
}: IProps) {
  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      className={twMerge(['relative z-50', className])}
      data-testid={dataTestId}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            data-testid={`${dataTestId}-panel`}
          >
            <div
              className="sm:flex sm:items-start"
              data-testid={`${dataTestId}-content`}
            >
              <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10">
                {variant === 'success' && (
                  <CheckCircleIcon
                    aria-hidden="true"
                    className="size-6 text-green-600"
                  />
                )}
                {variant === 'error' && (
                  <XCircleIcon
                    aria-hidden="true"
                    className="size-6 text-red-600"
                  />
                )}
                {variant === 'warning' && (
                  <ExclamationTriangleIcon
                    aria-hidden="true"
                    className="size-6 text-yellow-600"
                  />
                )}
                {variant === 'info' && (
                  <ExclamationCircleIcon
                    aria-hidden="true"
                    className="size-6 text-blue-600"
                  />
                )}
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold text-gray-900"
                >
                  {title}
                </DialogTitle>
                {description && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">{description}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              {onConfirm && (
                <button
                  data-testid={`${dataTestId}-confirm-button`}
                  type="button"
                  onClick={onConfirm}
                  className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                >
                  {confirmLabel}
                </button>
              )}
              <button
                type="button"
                data-testid={`${dataTestId}-close-button`}
                data-autofocus
                onClick={() => onClose(false)}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              >
                {closeLabel}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
